import React, { useContext, type FC } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';
import SectionMessage, { SectionMessageAction } from '@atlaskit/section-message';
import EditIcon from '@atlaskit/icon/core/edit';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import WorldIcon from '@atlaskit/icon/core/globe';

import { PublicLinksUpsell } from '@confluence/change-edition/entry-points/PublicLinksUpsell';
import { ChangeEditionSectionMessageShareAndRestrict } from '@confluence/change-edition/entry-points/ShareAndRestrictDialogUpsells';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';

import { PublishModalInfoBox, type PublishModalInfoBoxProps } from './PublishModalInfoBox';
import { InfoBox } from './InfoBox';
import { AskToEditBox, type AskToEditBoxProps } from './AskToEditBox';

const smartLinkWarningI18n = defineMessages({
	title: {
		id: 'share-and-restrict-dialog.info-message.smart-links-warning.title',
		defaultMessage: 'Additional restrictions can’t be set for smart links',
		description:
			'Section heading for a warning message that appears when the user is sharing a smart link',
	},
	message: {
		id: 'share-and-restrict-dialog.info-message.smart-links-warning.message',
		defaultMessage:
			'Anyone with access to this item in Confluence can view it, but permissions on the link may affect who can engage with the content.',
		description:
			'Body text for a warning message that appears when the user is sharing a smart link',
	},
	action: {
		id: 'share-and-restrict-dialog.info-message.smart-links-warning.action',
		defaultMessage: 'How do permissions work for Smart Links in the content tree',
		description:
			'Action link text for a warning message that appears when the user is sharing a smart link',
	},
});

const uneditableRestrictionsI18n = defineMessages({
	message: {
		id: 'share-and-restrict-dialog.info-message.uneditable-restrictions.message',
		defaultMessage: 'You don’t have permission to change access to this content.',
		description:
			'Body text for a message that appears when the user is unable to edit restrictions',
	},
});

const draftI18n = defineMessages({
	message: {
		id: 'share-and-restrict-dialog.info-message.draft.message',
		defaultMessage: 'Only people with the link can view and edit drafts.',
		description:
			'Body text for a message that appears when the user is viewing restrictions for a Draft page',
	},
	beforeLabel: {
		id: 'share-and-restrict-dialog.info-message.draft.before-label',
		defaultMessage: 'Draft info message',
		description: 'Label for the icon shown before the draft info message',
	},
	afterLabel: {
		id: 'share-and-restrict-dialog.info-message.draft.after-label',
		defaultMessage: 'More info tooltip',
		description:
			'Label for the icon shown after the draft info message which has a tooltip with further information',
	},
	tooltipMessage: {
		id: 'share-and-restrict-dialog.info-message.draft.tooltip-message',
		defaultMessage:
			'Only the draft’s creator has access to their draft until they share the link with someone else.',
		description: 'Tooltip message for the Info icon shown after the draft info message',
	},
});

const publicLinkUpsellI18n = defineMessages({
	iconLabel: {
		id: 'share-and-restrict-dialog.info-message.public-link-free-upsell.icon-label',
		defaultMessage: 'Public link',
		description: 'Label for the world icon in the public link upsell section',
	},
});

const freeUpsellBanner = xcss({
	borderRadius: 'radius.small',
	boxShadow: 'elevation.shadow.raised',
});

const upsellContainerStyles = xcss({
	padding: 'space.150',
});

const SMART_LINKS_LEARN_MORE_LINK =
	'https://support.atlassian.com/confluence-cloud/docs/bring-all-your-work-into-confluence/#Restrictions-and-Smart-Links-in-the-content-tree';

export type InfoMessageProps = {
	messageType?:
		| 'freeEditionPublicLinkUpsell'
		| 'freeTrialPublicLinkUpsell'
		| 'freeEditionUpsell'
		| 'smartLink'
		| 'uneditableRestrictions'
		| 'publishModal'
		| 'draft'
		| 'askToEdit';
	publishModalInfoBoxProps?: PublishModalInfoBoxProps;
	askToEditBoxProps?: AskToEditBoxProps;
};

export const InfoMessage: FC<InfoMessageProps> = ({
	messageType,
	publishModalInfoBoxProps,
	askToEditBoxProps,
}) => {
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const renderMessage = () => {
		switch (messageType) {
			case 'freeEditionPublicLinkUpsell':
			case 'freeTrialPublicLinkUpsell':
				return (
					<Inline space="space.100" xcss={[freeUpsellBanner, upsellContainerStyles]}>
						<WorldIcon label={formatMessage(publicLinkUpsellI18n.iconLabel)} spacing="spacious" />
						<PublicLinksUpsell isSiteAdmin={isSiteAdmin} upsellSource="share-and-restrict-dialog" />
					</Inline>
				);
			case 'freeEditionUpsell':
				return (
					<Box xcss={freeUpsellBanner}>
						<ChangeEditionSectionMessageShareAndRestrict />
					</Box>
				);
			case 'smartLink':
				return (
					<SectionMessage
						appearance="warning"
						title={formatMessage(smartLinkWarningI18n.title)}
						actions={[
							<SectionMessageAction
								key="action1"
								onClick={() => {
									window.open(SMART_LINKS_LEARN_MORE_LINK, '_blank');
								}}
							>
								{formatMessage(smartLinkWarningI18n.action)}
							</SectionMessageAction>,
						]}
					>
						<FormattedMessage {...smartLinkWarningI18n.message} />
					</SectionMessage>
				);
			case 'uneditableRestrictions':
				return (
					<SectionMessage appearance="information">
						<FormattedMessage {...uneditableRestrictionsI18n.message} />
					</SectionMessage>
				);
			case 'publishModal':
				return (
					<>{publishModalInfoBoxProps && <PublishModalInfoBox {...publishModalInfoBoxProps} />}</>
				);
			case 'draft':
				return (
					<InfoBox
						text={formatMessage(draftI18n.message)}
						beforeLabel={formatMessage(draftI18n.beforeLabel)}
						BeforeIcon={EditIcon}
						afterLabel={formatMessage(draftI18n.afterLabel)}
						AfterIcon={StatusInformationIcon}
						tooltipMessage={formatMessage(draftI18n.tooltipMessage)}
					/>
				);
			case 'askToEdit':
				return askToEditBoxProps ? <AskToEditBox {...askToEditBoxProps} /> : null;
		}
	};

	return <>{messageType && <Box testId="info-message-test-id">{renderMessage()}</Box>}</>;
};
